// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Switch, Route, Link, Redirect,
} from 'react-router-dom';
import moment from 'moment';

import PageHeader from '../../PageHeader.jsx';
import ContentBox from '../../ContentBox.jsx';
import { PrimaryText } from '../../Typography.jsx';
import { Button, PrimaryButton, DeleteButton } from '../../Buttons.jsx';

import ProblemSummary from './ProblemSummary.jsx';
import ProblemMedicationTable from './ProblemMedicationTable.jsx';
import ProblemInterventionTable from './ProblemInterventionTable.jsx';
import VitalSignsTable from '../vitals/VitalSignsTable.jsx';
import ViewNote from './view/ViewNote.jsx';
import { EditNurseNote, EditProviderNote } from './edit/EditNote.jsx';
import ViewSymptomAnalysis from './view/ViewSymptomAnalysis.jsx';
import EditSymptomAnalysis from './edit/EditSymptomAnalysis.jsx';
import ViewSystemAssessment from './view/ViewSystemAssessment.jsx';
import EditSystemAssessment from './edit/EditSystemAssessment.jsx';

import styles from '../../../styles/patientPages/progressNote/progressNote.scss';

import {
  deleteNurseNote,
  deleteProviderNote,
  deleteSymptomAnalysis,
  deleteSystemAssessment,
} from '../../../actions/progressNoteActions';

import {
  NURSE_NOTE_TYPE,
  PROVIDER_NOTE_TYPE,
  SYMPTOM_ANALYSIS_TYPE,
  SYSTEM_ASSESSMENT_TYPE,
} from './progressNoteTypes';

type Props = {
  match: Object,
  history: Object,
  problemIdToProblem: Object,
  problemIdToMeds: Object,
  problemIdToInterventions: Object,
  problemIdToProgressNotes: Object,
  handleDeleteNurseNote: Function,
  handleDeleteProviderNote: Function,
  handleDeleteSymptomAnalysis: Function,
  handleDeleteSystemAssessment: Function,
  activePatientId: string,
}

type State = {
  activeNote: ?Object,
  activeNoteType: string,
  viewOnly: boolean,
}

class TreatmentChartPageComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      activeNote: null,
      activeNoteType: '',
      viewOnly: true,
    };
  }

  // Page state handlers, activeNoteType set appropriately if active note, else default view
  handleEditNote = (note: Object) => () => {
    this.setState(
      {
        activeNote: note,
        activeNoteType: note.type,
      },
      () => {
        if (note.type === NURSE_NOTE_TYPE) {
          this.props.history.push(`${this.props.match.url}/nurseNote/edit`);
        } else if (note.type === PROVIDER_NOTE_TYPE) {
          this.props.history.push(`${this.props.match.url}/providerNote/edit`);
        } else if (note.type === SYMPTOM_ANALYSIS_TYPE) {
          this.props.history.push(`${this.props.match.url}/symptomAnalysis/edit`);
        } else if (note.type === SYSTEM_ASSESSMENT_TYPE) {
          this.props.history.push(`${this.props.match.url}/systemAssessment/edit`);
        }
      },
    );
  }

  handleViewNote = (note: Object) => () => {
    this.setState(
      {
        activeNote: note,
        activeNoteType: note.type,
      },
      () => {
        if (note.type === NURSE_NOTE_TYPE) {
          this.props.history.push(`${this.props.match.url}/nurseNote/view`);
        } else if (note.type === PROVIDER_NOTE_TYPE) {
          this.props.history.push(`${this.props.match.url}/providerNote/view`);
        } else if (note.type === SYMPTOM_ANALYSIS_TYPE) {
          this.props.history.push(`${this.props.match.url}/symptomAnalysis/view`);
        } else if (note.type === SYSTEM_ASSESSMENT_TYPE) {
          this.props.history.push(`${this.props.match.url}/systemAssessment/view`);
        }
      },
    );
  }

  handleAddNote = (type: string) => () => {
    this.setState(
      {
        activeNote: null,
        activeNoteType: type,
      },
      () => {
        if (type === NURSE_NOTE_TYPE) {
          this.props.history.push(`${this.props.match.url}/nurseNote/edit`);
        } else if (type === PROVIDER_NOTE_TYPE) {
          this.props.history.push(`${this.props.match.url}/providerNote/edit`);
        } else if (type === SYMPTOM_ANALYSIS_TYPE) {
          this.props.history.push(`${this.props.match.url}/symptomAnalysis/edit`);
        } else if (type === SYSTEM_ASSESSMENT_TYPE) {
          this.props.history.push(`${this.props.match.url}/systemAssessment/edit`);
        }
      },
    );
  }

  handleDeleteNote = (note: Object) => () => {
    switch (note.type) {
      case NURSE_NOTE_TYPE:
        this.props.handleDeleteNurseNote(this.props.activePatientId, note._id);
        break;
      case PROVIDER_NOTE_TYPE:
        this.props.handleDeleteProviderNote(this.props.activePatientId, note._id);
        break;
      case SYMPTOM_ANALYSIS_TYPE:
        this.props.handleDeleteSymptomAnalysis(this.props.activePatientId, note._id);
        break;
      case SYSTEM_ASSESSMENT_TYPE:
        this.props.handleDeleteSystemAssessment(this.props.activePatientId, note._id);
        break;
      default:
        break;
    }
  }

  closePage = () => {
    this.setState({
      activeNote: null,
      activeNoteType: '',
    }, () => {
      this.props.history.push(`${this.props.match.url}`);
    });
  }

  render() {
    const {
      problemIdToProblem, problemIdToInterventions, problemIdToMeds, problemIdToProgressNotes, match,
    } = this.props;
    const problem = problemIdToProblem[match.params.problemId];
    if (!problem) {
      return <Redirect to='../currentProblems' />;
    }

    let treatments = [];
    let medications = [];
    let interventions = [];
    if (problem._id in problemIdToMeds) {
      treatments = treatments.concat(problemIdToMeds[problem._id].map(
        med => <div key={med._id}>{med.drugName}</div>,
      ));
      medications = problemIdToMeds[problem._id];
    }
    if (problem._id in problemIdToInterventions) {
      treatments = treatments.concat(problemIdToInterventions[problem._id].map(
        intervention => <div key={intervention._id}>{intervention.name}</div>,
      ));
      interventions = problemIdToInterventions[problem._id];
    }

    let progressNotes = [];
    if (problem._id in problemIdToProgressNotes) {
      progressNotes = progressNotes.concat(problemIdToProgressNotes[problem._id].map(
        note => <ContentBox key={note._id}>
          <div className={styles.problemCard}>
            <span className={styles.problemCardInfo}>
              <PrimaryText>{note.type}</PrimaryText> <br />
              {note.date && moment(note.date).format('DD-MM-YYYY, HH:mm')}
            </span>
            <span className={styles.problemCardNote}>{note.notes}</span>
            <span className={styles.problemCardActions}>
              {/* "View" button commented out, since we used to display note preview but now we display entire note. */}
              {/* <Button onClick={this.handleViewNote(note)}>View</Button> */}
              <PrimaryButton onClick={this.handleEditNote(note)}>Edit</PrimaryButton>
              <DeleteButton onClick={this.handleDeleteNote(note)}>X</DeleteButton>
            </span>
          </div>
        </ContentBox>,
      ));
    }

    const mainDashboard = (
      <div>
        <PageHeader title={`Treatments & Chart - ${problem.diagnosis || 'Unknown Diagnosis'}`} />
        <ContentBox>
          <Link to={`../${problem.resolutionDate ? 'resolved' : 'current'}Problems`}>
            <Button>Back to All Dx/Tx</Button>
          </Link>
          <PrimaryButton onClick={this.handleAddNote(NURSE_NOTE_TYPE)}>
            Add Nurse Note
          </PrimaryButton>
          <PrimaryButton onClick={this.handleAddNote(PROVIDER_NOTE_TYPE)}>
            Add Provider Note
          </PrimaryButton>
          <PrimaryButton onClick={this.handleAddNote(SYMPTOM_ANALYSIS_TYPE)}>
            Add Symptom Analysis
          </PrimaryButton>
          <PrimaryButton onClick={this.handleAddNote(SYSTEM_ASSESSMENT_TYPE)}>
            Add System Assessment
          </PrimaryButton>
        </ContentBox>
        <ProblemSummary
          problem={problem}
          treatments={treatments}
        />
        <ContentBox title='Medications'>
          <ProblemMedicationTable
            medications={medications}
            problemId={problem._id}
            acute={problem.acute}
            chronic={problem.chronic}
          />
        </ContentBox>
        <ContentBox title='Interventions'>
          <ProblemInterventionTable
            interventions={interventions}
            problemId={problem._id}
            acute={problem.acute}
            chronic={problem.chronic}
          />
        </ContentBox>
        <ContentBox title='Vital Signs'>
          <VitalSignsTable problem={problem} />
        </ContentBox>
        <PageHeader title='Progress Notes' />
        {progressNotes.length ? progressNotes : <ContentBox title='No progress notes' />}
      </div>
    );

    const { url } = this.props.match;
    const { activeNote, activeNoteType } = this.state;

    return (
      <Switch>
        <Route exact path={`${url}`} render={() => mainDashboard} />
        <Route exact path={`${url}/nurseNote/edit`} render={() => (
          <EditNurseNote
            note={activeNote}
            closePage={this.closePage}
            problemId={problem._id}
          />)}
        />
        <Route exact path={`${url}/nurseNote/view`} render={() => (
          <ViewNote
            note={activeNote || {}}
            closePage={this.closePage}
            type={activeNoteType}
          />)}
        />
        <Route exact path={`${url}/providerNote/edit`} render={() => (
          <EditProviderNote
            note={activeNote}
            closePage={this.closePage}
            problemId={problem._id}
          />)}
        />
        <Route exact path={`${url}/providerNote/view`} render={() => (
          <ViewNote
            note={activeNote || {}}
            closePage={this.closePage}
            type={activeNoteType}
          />)}
        />
        <Route exact path={`${url}/symptomAnalysis/edit`} render={() => (
          <EditSymptomAnalysis
            note={activeNote}
            closePage={this.closePage}
            problemId={problem._id}
          />)}
        />
        <Route exact path={`${url}/symptomAnalysis/view`} render={() => (
          <ViewSymptomAnalysis
            note={activeNote || {}}
            closePage={this.closePage}
          />)}
        />
        <Route exact path={`${url}/systemAssessment/edit`} render={() => (
          <EditSystemAssessment
            note={activeNote}
            closePage={this.closePage}
            problemId={problem._id}
          />)}
        />
        <Route exact path={`${url}/systemAssessment/view`} render={() => (
          <ViewSystemAssessment
            note={activeNote || {}}
            closePage={this.closePage}
          />)}
        />
      </Switch>
    );
  }
}

const mapStateToProps = state => ({
  problemIdToProblem: state.patient.data.problemIdToProblem,
  problemIdToMeds: state.patient.data.problemIdToMeds,
  problemIdToInterventions: state.patient.data.problemIdToInterventions,
  problemIdToProgressNotes: state.patient.data.problemIdToProgressNotes,
  activePatientId: state.patient.data.activePatientId,
});

const mapDispatchToProps = dispatch => ({
  handleDeleteSymptomAnalysis: (patientId, symptomAnalysisId) => dispatch(
    deleteSymptomAnalysis(patientId, symptomAnalysisId),
  ),
  handleDeleteSystemAssessment: (patientId, systemAssessmentId) => dispatch(
    deleteSystemAssessment(patientId, systemAssessmentId),
  ),
  handleDeleteProviderNote: (patientId, providerNoteId) => dispatch(
    deleteProviderNote(patientId, providerNoteId),
  ),
  handleDeleteNurseNote: (patientId, nurseNoteId) => dispatch(
    deleteNurseNote(patientId, nurseNoteId),
  ),
});

const TreatmentChartPage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TreatmentChartPageComponent);

export default TreatmentChartPage;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/progressNote/TreatmentChartPage.jsx